import React, { useMemo, useRef, useCallback } from 'react';
import { ColorPicker, Segmented, Select, Tooltip } from 'antd';

// antd ColorPicker's built-in single/gradient mode switch fights a controlled
// solid value (the tab resets on every re-render), and its presets can't parse
// gradient strings. So this component owns the solid/gradient split itself and
// only uses ColorPicker for picking plain colors.
const GRADIENT_PRESETS = [
    { name: 'Sunrise', value: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' },
    { name: 'Gold', value: 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)' },
    { name: 'Silver', value: 'linear-gradient(135deg, #e0e0e0 0%, #9e9e9e 100%)' },
    { name: 'Bronze', value: 'linear-gradient(135deg, #cd7f32 0%, #8b4513 100%)' },
    { name: 'Royal', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { name: 'Blossom', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { name: 'Ocean', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { name: 'Mint', value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
];

const SOLID_PRESETS = [
    { label: 'Solid colors', colors: ['#1677ff', '#722ed1', '#52c41a', '#faad14', '#ff4d4f', '#111111', '#ffffff'] },
];

const ANGLE_OPTIONS = [
    { value: 90, label: 'Left to right' },
    { value: 135, label: 'Diagonal down' },
    { value: 180, label: 'Top to bottom' },
    { value: 45, label: 'Diagonal up' },
];

type Parsed =
    | { kind: 'solid'; color: string }
    | { kind: 'gradient'; angle: number; from: string; to: string };

const COLOR_RE = /(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\))(\s+[\d.]+%)?/g;

const parseBg = (v?: string): Parsed => {
    if (!v || !v.trim()) return { kind: 'solid', color: '#1677ff' };
    if (/linear-gradient/i.test(v)) {
        const angleM = v.match(/linear-gradient\(\s*([\d.]+)deg/i);
        const colors = (v.match(COLOR_RE) || []).map(c => c.replace(/\s+[\d.]+%$/, ''));
        if (colors.length >= 2) {
            return { kind: 'gradient', angle: angleM ? parseFloat(angleM[1]) : 135, from: colors[0], to: colors[colors.length - 1] };
        }
        return { kind: 'gradient', angle: 135, from: '#f6d365', to: '#fda085' };
    }
    return { kind: 'solid', color: v };
};

interface BackgroundPickerProps {
    /** CSS background value — a solid color or a linear-gradient string. */
    value?: string;
    onChange?: (value: string) => void;
}

/**
 * Form-friendly background picker: solid colors and linear gradients without
 * typing raw CSS. Emits a plain CSS background string, so it drops into any
 * Form.Item that previously used a free-text CSS input.
 */
const BackgroundPicker: React.FC<BackgroundPickerProps> = ({ value, onChange }) => {
    const parsed = useMemo(() => parseBg(value), [value]);

    const emitGradient = useCallback((g: { angle: number; from: string; to: string }) =>
        onChange?.(`linear-gradient(${g.angle}deg, ${g.from} 0%, ${g.to} 100%)`), [onChange]);

    // Live-update the preview bar during drag without re-rendering the ColorPicker.
    // Updating the ColorPicker's value prop during drag causes its internal
    // useEffect to recalculate the handle position from the color (HSB round-trip),
    // which fights the mouse position and makes the pointer jump.
    const previewRef = useRef<HTMLDivElement>(null);

    const updatePreview = useCallback((css: string) => {
        if (previewRef.current) previewRef.current.style.background = css;
    }, []);

    const angleValue = ANGLE_OPTIONS.some(o => o.value === (parsed as any).angle)
        ? (parsed as any).angle
        : 135;

    return (
        <div className="space-y-2">
            <Segmented
                size="small"
                value={parsed.kind}
                onChange={(k) => {
                    if (k === parsed.kind) return;
                    if (k === 'solid') {
                        onChange?.(parsed.kind === 'gradient' ? parsed.from : '#1677ff');
                    } else {
                        emitGradient({ angle: 135, from: parsed.kind === 'solid' ? parsed.color : '#f6d365', to: '#fda085' });
                    }
                }}
                options={[{ label: 'Solid', value: 'solid' }, { label: 'Gradient', value: 'gradient' }]}
            />

            {parsed.kind === 'solid' ? (
                <ColorPicker
                    value={parsed.color}
                    onChange={c => updatePreview(c.toHexString())}
                    onChangeComplete={c => onChange?.(c.toHexString())}
                    presets={SOLID_PRESETS}
                    showText
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                />
            ) : (
                <div className="flex items-center gap-2 flex-wrap">
                    <Tooltip title="Start color">
                        <ColorPicker
                            value={parsed.from}
                            onChange={c => updatePreview(
                                `linear-gradient(${(parsed as any).angle ?? 135}deg, ${c.toHexString()} 0%, ${(parsed as any).to} 100%)`
                            )}
                            onChangeComplete={c => emitGradient({ ...parsed as any, from: c.toHexString() })}
                        />
                    </Tooltip>
                    <span className="text-xs text-muted-foreground">to</span>
                    <Tooltip title="End color">
                        <ColorPicker
                            value={parsed.to}
                            onChange={c => updatePreview(
                                `linear-gradient(${(parsed as any).angle ?? 135}deg, ${(parsed as any).from} 0%, ${c.toHexString()} 100%)`
                            )}
                            onChangeComplete={c => emitGradient({ ...parsed as any, to: c.toHexString() })}
                        />
                    </Tooltip>
                    <Select
                        size="small"
                        value={angleValue}
                        onChange={a => emitGradient({ ...parsed as any, angle: a })}
                        options={ANGLE_OPTIONS}
                        style={{ width: 140 }}
                    />
                </div>
            )}

            {/* Live preview of the current background */}
            <div ref={previewRef} className="h-6 rounded-md border border-border" style={{ background: value || '#1677ff' }} />

            {/* One-click gradient presets */}
            <div className="flex flex-wrap gap-1.5">
                {GRADIENT_PRESETS.map(g => (
                    <Tooltip title={g.name} key={g.name}>
                        <button
                            type="button"
                            aria-label={`${g.name} gradient`}
                            onClick={() => onChange?.(g.value)}
                            className="cursor-pointer transition-transform hover:scale-110"
                            style={{
                                width: 24, height: 24, borderRadius: 6, background: g.value, padding: 0,
                                border: value === g.value ? '2px solid var(--primary)' : '1px solid var(--border)',
                            }}
                        />
                    </Tooltip>
                ))}
            </div>
        </div>
    );
};

export default BackgroundPicker;
